import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiErrorMessage } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((r) => mounted && setUser(r.data))
      .catch(() => mounted && setUser(false))
      .finally(() => mounted && setReady(true));
    return () => {
      mounted = false;
    };
  }, []);

  async function login(email, password, otp) {
    try {
      const body = { email, password };
      if (otp) body.otp = otp;
      const { data } = await api.post("/auth/login", body);
      setUser(data);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.require_2fa) {
        return { ok: false, require_2fa: true, error: detail.message || "OTP required" };
      }
      return { ok: false, error: apiErrorMessage(e) };
    }
  }

  async function register(name, email, password) {
    try {
      const { data } = await api.post("/auth/register", {
        name,
        email,
        password,
      });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    setUser(false);
  }

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
